// TanStack Query hooks for the RBAC API.
//
// One file per concern (queries vs mutations) was tempting but they share the
// query keys, so co-locating prevents drift. ~150 LOC; if it grows past 300
// split into queries.ts / mutations.ts.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import {
  assignUserRoles,
  createRole,
  createTenantUser,
  deleteRole,
  getMyPermissions,
  getRole,
  listBranches,
  listPermissions,
  listRoles,
  listUsers,
  resendUserInvite,
  updateRole,
  updateUserBranch,
  type BranchSummary,
  type CreateTenantUserPayload,
  type EffectiveGrant,
  type ListUsersParams,
  type PaginatedResponse,
  type PermissionDef,
  type Role,
  type RoleWritePayload,
  type UserListEntry,
} from "@/lib/api/rbac";

// Cache key prefix. Centralized so invalidation can be done from anywhere
// (e.g. a global error handler) without typo'd keys leaking through.
export const RBAC_KEYS = {
  all: ["rbac"] as const,
  permissions: () => [...RBAC_KEYS.all, "permissions"] as const,
  roles: () => [...RBAC_KEYS.all, "roles"] as const,
  role: (id: string) => [...RBAC_KEYS.roles(), id] as const,
  users: (params: ListUsersParams = {}) => [...RBAC_KEYS.all, "users", params] as const,
  branches: () => [...RBAC_KEYS.all, "branches"] as const,
  myPermissions: () => [...RBAC_KEYS.all, "me-permissions"] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function usePermissionsCatalog() {
  return useQuery<PermissionDef[]>({
    queryKey: RBAC_KEYS.permissions(),
    queryFn: listPermissions,
    // Catalog is application-static (Python-side, no DB hit). One hour stale
    // window means at most one refetch per hour even with constant remounts.
    staleTime: 60 * 60 * 1000,
  });
}

export function useRolesQuery() {
  return useQuery<Role[]>({
    queryKey: RBAC_KEYS.roles(),
    queryFn: listRoles,
  });
}

export function useRoleQuery(roleId: string | undefined) {
  return useQuery<Role>({
    queryKey: roleId ? RBAC_KEYS.role(roleId) : ["rbac", "roles", "noop"],
    queryFn: () => getRole(roleId as string),
    enabled: Boolean(roleId),
  });
}

export function useUsersQuery(params: ListUsersParams = {}) {
  return useQuery<PaginatedResponse<UserListEntry>>({
    queryKey: RBAC_KEYS.users(params),
    queryFn: () => listUsers(params),
  });
}

export function useBranchesQuery() {
  return useQuery<BranchSummary[]>({
    queryKey: RBAC_KEYS.branches(),
    queryFn: listBranches,
    // Branches change rarely. 5 min stale window keeps the user-create
    // form's select fresh without thrashing the API on every navigation.
    staleTime: 5 * 60 * 1000,
  });
}

export function useMyPermissionsQuery() {
  return useQuery<EffectiveGrant[]>({
    queryKey: RBAC_KEYS.myPermissions(),
    queryFn: getMyPermissions,
    // Backend invalidates the cache on writes (PermissionsVersion bump), but
    // the frontend doesn't know about that signal directly. Refetch on focus
    // keeps the UI honest after admin-side edits in another tab.
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function useInvalidateRbac() {
  const qc = useQueryClient();
  // After any RBAC write the safe move is "blow away the whole subtree".
  // Granular invalidation is brittle (a role rename also affects user list
  // role chips, the catalog never invalidates, etc.). The whole subtree is
  // bounded — at most ~6 queries — so the cost is negligible.
  return () => qc.invalidateQueries({ queryKey: RBAC_KEYS.all });
}

export function useCreateRoleMutation(): UseMutationResult<Role, Error, RoleWritePayload> {
  const invalidate = useInvalidateRbac();
  return useMutation({
    mutationFn: (body: RoleWritePayload) => createRole(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateRoleMutation(
  roleId: string,
): UseMutationResult<Role, Error, Partial<RoleWritePayload>> {
  const invalidate = useInvalidateRbac();
  return useMutation({
    mutationFn: (body: Partial<RoleWritePayload>) => updateRole(roleId, body),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteRoleMutation(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateRbac();
  return useMutation({
    mutationFn: (roleId: string) => deleteRole(roleId),
    onSuccess: () => invalidate(),
  });
}

export function useAssignUserRolesMutation(
  userId: number,
): UseMutationResult<{ roles: string[] }, Error, string[]> {
  const invalidate = useInvalidateRbac();
  return useMutation({
    mutationFn: (roleIds: string[]) => assignUserRoles(userId, roleIds),
    onSuccess: () => invalidate(),
  });
}

export function useCreateTenantUserMutation(): UseMutationResult<
  UserListEntry,
  Error,
  CreateTenantUserPayload
> {
  const invalidate = useInvalidateRbac();
  return useMutation({
    mutationFn: (body: CreateTenantUserPayload) => createTenantUser(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateUserBranchMutation(
  userId: number,
): UseMutationResult<UserListEntry, Error, string | null> {
  const invalidate = useInvalidateRbac();
  return useMutation({
    mutationFn: (branchId: string | null) => updateUserBranch(userId, branchId),
    onSuccess: () => invalidate(),
  });
}

// GYM-1578: re-issue invite email
export function useResendUserInviteMutation(
  userId: number,
): UseMutationResult<{ detail: string }, Error, void> {
  return useMutation({
    mutationFn: () => resendUserInvite(userId),
  });
}
